import { useMemo } from "react";
import { useRecordContext } from "react-admin";
import type { EditorField } from "@/components/common/EditableGrid";
import {
  Claimant,
  StatusData,
  EnrollmentData,
  PolicyData,
  ResourceName,
  CLAIMANT_GENDER_CHOICES,
  CLAIMANT_RELATIONSHIP_CHOICES,
  DISABILITY_TYPE_CHOICES,
  FMLA_TYPE_CHOICES,
  MEDICARE_QUALIFYING_EVENT_CHOICES,
  COBRA_QUALIFYING_EVENT_CHOICES,
  PREMIUM_PAYER_CHOICES,
} from "@/types";
import { validateZipCode } from "@/utils/validators/inPlaceValidators";
import { useSelectedPolicyData } from "../hooks/useCurrentPolicyData";

/** Extended claimant with flattened policy data for EditableGrid compatibility */
export interface ClaimantWithFlattenedPolicy extends Claimant {
  current_policy_data?: PolicyData;
  status?: StatusData;
  enrollment?: EnrollmentData;
  _selectedPolicyId?: string;
}

/**
 * Field definitions and the flattened-record hook for the claimant Details tab
 * (`details/ClaimantDetailsTab.tsx`, Feature #7659). The Overview tab component
 * that used to live here was replaced by the Details tab; the declarative field
 * config stays here because its shape test and the dependent/subscriber rules
 * reference it.
 *
 * Returns claimant record with policy data flattened for EditableGrid.
 * This enables field paths like "status.is_fmla_used" to work with InPlace editors.
 * Includes _selectedPolicyId so dataProvider knows which policy to update.
 *
 * Uses nullish coalescing (??) so that optimistic updates from InPlaceEditor
 * (which set flattened fields directly on the cached record) are preserved
 * rather than being overwritten by stale nested policy_data values.
 */
export const useClaimantWithFlattenedPolicy = (): ClaimantWithFlattenedPolicy | undefined => {
  const claimant = useRecordContext<Claimant>();
  const { policyData, policyId } = useSelectedPolicyData();

  return useMemo(() => {
    if (!claimant) return undefined;

    // During optimistic updates the cached record already has flattened
    // status/enrollment from InPlaceEditor. Prefer those over re-extracting
    // from nested policy_data to avoid overwriting in-flight edits.
    const cached = claimant as ClaimantWithFlattenedPolicy;

    // Only prefer cached values when they belong to the currently selected
    // policy. If the user switched policies while an edit was in flight, the
    // stale cached data from the previous policy should be discarded.
    const isCurrentPolicy = cached._selectedPolicyId === policyId;

    return {
      ...claimant,
      current_policy_data: isCurrentPolicy ? (cached.current_policy_data ?? policyData) : policyData,
      status: isCurrentPolicy ? (cached.status ?? policyData?.status) : policyData?.status,
      enrollment: isCurrentPolicy ? (cached.enrollment ?? policyData?.enrollment) : policyData?.enrollment,
      _selectedPolicyId: policyId,
    };
  }, [claimant, policyData, policyId]);
};

// ─────────────────────────────────────────────────────────────
// Field definitions
// ─────────────────────────────────────────────────────────────

/**
 * Explanatory copy for the Member Identifiers section (#6515).
 *
 * Two loads: the first sentence stops a user "fixing" a mismatch that is
 * correct, the second is the only place the UI states that editing an id changes
 * identity resolution — #6514 made the field writable and the confirmation step
 * was cut from scope, so this is the guardrail.
 *
 * "may issue" / "may not match" rather than "will": a bundled vendor issues ONE
 * id across both lines, and even on a carve-out the two can coincide. "map"
 * rather than "match" because match is the matcher's term of art.
 *
 * Wording approved by Blake. Still owed a business-side review per the AC.
 */
export const MEMBER_IDENTIFIERS_NOTE =
  "The medical TPA and the PBM may issue separate ids, so these may not match. " +
  "Editing either affects how future documents map to this claimant.";

export const SECTION_DESCRIPTIONS: Record<string, string> = {
  "Member Identifiers": MEMBER_IDENTIFIERS_NOTE,
};

// Exported for src/tests/unit/resources/claimants/ClaimantOverviewTab.test.ts,
// mirroring policyOverviewFields (PolicyOverviewTab.tsx). Declarative field
// config is exactly what a shape test catches cheaply — both #6515 defects
// (showWhen hiding the field un-addably, and crediting a helperText that never
// rendered) were declarative-config behaviour (Dillon review).
export const overviewFields: EditorField[] = [
  {
    type: "reference",
    source: "client_id_ref",
    reference: ResourceName.CLIENTS,
    displayField: "client_legal_name",
  },
  { source: "claimant_first_name" },
  { source: "claimant_last_name" },
  { source: "claimant_gender", type: "select", choices: CLAIMANT_GENDER_CHOICES },
  { source: "claimant_birth_date", type: "date" },
  { source: "claimant_age", type: "number" },
  { source: "claimant_relationship", type: "select", choices: CLAIMANT_RELATIONSHIP_CHOICES },
  { source: "claimant_related_subscriber_id" },
  { source: "claimant_related_subscriber_first_name" },
  { source: "claimant_related_subscriber_last_name" },
  {
    source: "claimant_related_subscriber_name",
    readOnlyReason: "Legacy composite field — view only; use first/last name fields above to edit",
  },
  // View-only (PO 2026-10-07): lasers are written only on the policy's Lasered
  // Claimants tab, so a claimant PATCH never carries laser state (#7012).
  {
    source: "current_policy_data.is_lasered",
    type: "boolean",
    readOnlyReason: "Lasers are managed on the policy's Lasered Claimants tab",
  },
  { source: "enrollment.is_dependent", type: "boolean" },
  // Member identifiers (#6515). Grouped and labelled by SOURCE rather than by
  // type, because on an Rx carve-out policy a claimant legitimately carries two
  // ids that do not match — e.g. 230499298 from UHC alongside 888008175-1 from
  // RxBenefits. Rendered as two unrelated "... Member ID" fields that reads like
  // a data error and invites someone to "fix" one of them.
  //
  // EXACTLY ONE thing conveys that holding both is correct: the section caption
  // in SECTION_DESCRIPTIONS above. Not three layers — the per-field route is
  // dead. EditableGrid sets `helperText: metaTitle` from allColumnsMetadata
  // (EditableGrid.tsx:297), then all SIX in-place editors hardcode
  // `helperText={validate ? undefined : false}` and discard it: Text:59, plus
  // Select / Boolean / Number / Date / State. So the `rx_member_id` tooltip in
  // allColumnsMetadata.json is dead copy inside this grid — it exists so the
  // string is right whenever that defect is fixed, not because it renders.
  //
  // Labels are overridden HERE rather than in allColumnsMetadata.json on purpose:
  // the metadata entry is shared, and relabelling member_id globally would change
  // every other claimant grid and table that renders it.
  { section: "Member Identifiers", source: "member_id", label: "Medical (TPA)" },
  // No showWhen: EditableGrid already hides empty fields by default, and hiding
  // this one unconditionally would make it correctable but never ADDABLE.
  //
  // The auto-hide predicate is `!valueExists && (!canEdit || !showAllFields)`,
  // at EditableGrid.tsx:245 (hidden count) and :280 (render). `showAllFields`
  // starts at `defaultShowEmptyFields`, which the Overview grid leaves at its
  // `false` default — so an empty pharmacy id is hidden regardless of whether the
  // user can edit it. Behaviour:
  //
  //   medical-only claimant, default view  -> hidden; tab looks exactly as today
  //   medical-only, "Show N empty fields"  -> appears empty and editable, so a
  //                                           human CAN add a known PBM id
  //   carve-out claimant                   -> always visible alongside Medical
  { section: "Member Identifiers", source: "rx_member_id", label: "Pharmacy (PBM)" },
];

/** Enrollment fields for employees/subscribers — own claimant_info + employee_info, all editable. */
export const overviewFieldsWithEmployeeEnrollment: EditorField[] = [
  ...overviewFields,
  { section: "Enrollment", source: "enrollment.employee_info.hire_date", type: "date" },
  { section: "Enrollment", source: "enrollment.claimant_info.original_effective_date", type: "date" },
  { section: "Enrollment", source: "enrollment.claimant_info.current_effective_date", type: "date" },
  { section: "Enrollment", source: "enrollment.claimant_info.plan_name" },
  { section: "Enrollment", source: "enrollment.claimant_info.zip_code", validate: validateZipCode },
  {
    section: "Enrollment",
    source: "enrollment.employee_info.premium_paid_by",
    type: "select",
    choices: PREMIUM_PAYER_CHOICES,
  },
];

/** Enrollment fields for dependents/spouses — own claimant_info (editable) +
 *  subscriber's employee_info (read-only — edit on the subscriber's record). */
export const overviewFieldsWithDependentEnrollment: EditorField[] = [
  ...overviewFields,
  { section: "Enrollment", source: "enrollment.claimant_info.original_effective_date", type: "date" },
  { section: "Enrollment", source: "enrollment.claimant_info.current_effective_date", type: "date" },
  { section: "Enrollment", source: "enrollment.claimant_info.plan_name" },
  { section: "Enrollment", source: "enrollment.claimant_info.zip_code", validate: validateZipCode },
  {
    section: "Subscriber Enrollment",
    source: "enrollment.employee_info.hire_date",
    type: "date",
    readOnlyReason: "Edit on the subscriber's record",
  },
  {
    section: "Subscriber Enrollment",
    source: "enrollment.employee_info.original_effective_date",
    type: "date",
    readOnlyReason: "Edit on the subscriber's record",
  },
  {
    section: "Subscriber Enrollment",
    source: "enrollment.employee_info.current_effective_date",
    type: "date",
    readOnlyReason: "Edit on the subscriber's record",
  },
  {
    section: "Subscriber Enrollment",
    source: "enrollment.employee_info.plan_name",
    readOnlyReason: "Edit on the subscriber's record",
  },
  {
    section: "Subscriber Enrollment",
    source: "enrollment.employee_info.premium_paid_by",
    type: "select",
    choices: PREMIUM_PAYER_CHOICES,
    readOnlyReason: "Edit on the subscriber's record",
  },
];

// ─── Shared eligibility field groups ──────────────────────────

const medicareFields: EditorField[] = [
  { section: "Medicare", source: "status.is_medicare_eligible", type: "boolean" },
  {
    section: "Medicare",
    source: "status.medicare.qualifying_event",
    type: "select",
    choices: MEDICARE_QUALIFYING_EVENT_CHOICES,
    showWhen: "status.is_medicare_eligible",
  },
  {
    section: "Medicare",
    source: "status.medicare.part_a_effective",
    type: "date",
    showWhen: "status.is_medicare_eligible",
  },
  {
    section: "Medicare",
    source: "status.medicare.part_b_effective",
    type: "date",
    showWhen: "status.is_medicare_eligible",
  },
  {
    section: "Medicare",
    source: "status.medicare.part_c_effective",
    type: "date",
    showWhen: "status.is_medicare_eligible",
  },
  {
    section: "Medicare",
    source: "status.medicare.part_d_effective",
    type: "date",
    showWhen: "status.is_medicare_eligible",
  },
];

const cobraFields: EditorField[] = [
  { section: "COBRA", source: "status.is_cobra_applicable", type: "boolean" },
  {
    section: "COBRA",
    source: "status.cobra.qualifying_event",
    type: "select",
    choices: COBRA_QUALIFYING_EVENT_CHOICES,
    showWhen: "status.is_cobra_applicable",
  },
  { section: "COBRA", source: "status.cobra.qualifying_date", type: "date", showWhen: "status.is_cobra_applicable" },
  { section: "COBRA", source: "status.cobra.election_date", type: "date", showWhen: "status.is_cobra_applicable" },
  {
    section: "COBRA",
    source: "status.cobra.max_coverage_months",
    type: "number",
    showWhen: "status.is_cobra_applicable",
  },
  { section: "COBRA", source: "status.cobra.monthly_premium", type: "dollar", showWhen: "status.is_cobra_applicable" },
  { section: "COBRA", source: "status.cobra.premiums_paid", type: "dollar", showWhen: "status.is_cobra_applicable" },
  {
    section: "COBRA",
    source: "status.cobra.coverage_dates",
    type: "dateRangeArray",
    showWhen: "status.is_cobra_applicable",
  },
];

// ─── Employee eligibility: full set ──────────────────────────

export const employeeEligibilityFields: EditorField[] = [
  // Employment
  { section: "Employment", source: "status.is_actively_working", type: "boolean" },
  {
    section: "Employment",
    source: "status.last_active_work_date",
    type: "date",
    showWhen: "!status.is_actively_working",
  },
  {
    section: "Employment",
    source: "status.return_to_work_date",
    type: "date",
    showWhen: "!status.is_actively_working",
  },
  { section: "Employment", source: "status.is_retired", type: "boolean" },
  { section: "Employment", source: "status.retirement_date", type: "date", showWhen: "status.is_retired" },

  // Medicare
  ...medicareFields,

  // Disability
  { section: "Disability", source: "status.is_on_disability", type: "boolean" },
  {
    section: "Disability",
    source: "status.disability.disability_type",
    type: "select",
    choices: DISABILITY_TYPE_CHOICES,
    showWhen: "status.is_on_disability",
  },
  {
    section: "Disability",
    source: "status.disability.disability_dates",
    type: "dateRangeArray",
    showWhen: "status.is_on_disability",
  },
  {
    section: "Disability",
    source: "status.disability.ssdi_effective_date",
    type: "date",
    showWhen: "status.is_on_disability",
  },

  // Sick Leave
  { section: "Sick Leave", source: "status.is_sick_leave_used", type: "boolean" },
  {
    section: "Sick Leave",
    source: "status.sick_leave_dates",
    type: "dateRangeArray",
    showWhen: "status.is_sick_leave_used",
  },

  // Vacation Leave
  { section: "Vacation Leave", source: "status.is_vacation_leave_used", type: "boolean" },
  {
    section: "Vacation Leave",
    source: "status.vacation_dates",
    type: "dateRangeArray",
    showWhen: "status.is_vacation_leave_used",
  },

  // FMLA
  { section: "FMLA", source: "status.is_fmla_used", type: "boolean" },
  {
    section: "FMLA",
    source: "status.fmla.fmla_type",
    type: "select",
    choices: FMLA_TYPE_CHOICES,
    showWhen: "status.is_fmla_used",
  },
  { section: "FMLA", source: "status.fmla.total_hours_used", type: "number", showWhen: "status.is_fmla_used" },
  { section: "FMLA", source: "status.fmla.total_hours_available", type: "number", showWhen: "status.is_fmla_used" },
  { section: "FMLA", source: "status.fmla.hours_scheduled_work", type: "number", showWhen: "status.is_fmla_used" },
  { section: "FMLA", source: "status.fmla.reason", showWhen: "status.is_fmla_used" },
  { section: "FMLA", source: "status.fmla.is_paid_leave", type: "boolean", showWhen: "status.is_fmla_used" },
  { section: "FMLA", source: "status.fmla.is_concurrent_with_loa", type: "boolean", showWhen: "status.is_fmla_used" },
  {
    section: "FMLA",
    source: "status.fmla.premium_paid_by",
    type: "select",
    choices: PREMIUM_PAYER_CHOICES,
    showWhen: "status.is_fmla_used",
  },
  { section: "FMLA", source: "status.fmla.premium_paid_through", type: "date", showWhen: "status.is_fmla_used" },
  { section: "FMLA", source: "status.fmla.leave_dates", type: "dateRangeArray", showWhen: "status.is_fmla_used" },

  // Medical LOA
  { section: "Medical LOA", source: "status.is_medical_loa_used", type: "boolean" },
  {
    section: "Medical LOA",
    source: "status.medical_loa.premium_paid_by",
    type: "select",
    choices: PREMIUM_PAYER_CHOICES,
    showWhen: "status.is_medical_loa_used",
  },
  {
    section: "Medical LOA",
    source: "status.medical_loa.premium_paid_through",
    type: "date",
    showWhen: "status.is_medical_loa_used",
  },
  {
    section: "Medical LOA",
    source: "status.medical_loa.leave_dates",
    type: "dateRangeArray",
    showWhen: "status.is_medical_loa_used",
  },

  // Termination
  { section: "Termination", source: "status.is_terminated", type: "boolean" },
  {
    section: "Termination",
    source: "status.employment_termination_date",
    type: "date",
    showWhen: "status.is_terminated",
  },
  {
    section: "Termination",
    source: "status.benefits_termination_date",
    type: "date",
    showWhen: "status.is_terminated",
  },

  // COBRA
  ...cobraFields,
];

// ─── Dependent eligibility: coverage-relevant fields only ────

export const dependentEligibilityFields: EditorField[] = [
  // Medicare (dependent can be Medicare-eligible independently)
  ...medicareFields,

  // Benefits termination (coverage can end for a dependent)
  { section: "Coverage", source: "status.benefits_termination_date", type: "date" },

  // COBRA (dependent may have independent COBRA rights)
  ...cobraFields,
];
